import { useState } from 'react';
import { useDrag } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import type { UseQueryResult } from '@tanstack/react-query';
import { styled } from 'goober';
import { isEmpty } from 'lodash-es';

import SvgFiles from '@/assets/widgets/empty-state-files.svg';
import { ActionPanelEmpty } from '@/features/action-panel/action-panel-empty';
import { withDraggableProvider } from '@/hoc/with-draggable-provider';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { FilesDragDrop } from '../../files-drag-drop/files-drag-drop';
import type {
  File,
  FileLinkDownloadHandler,
  FileScanning,
} from './file.interface';
import { AutomaticUploadFileButton } from './automatic-upload-file-button';
import type { ShareFilePayload } from './edit-file';
import { withPermission } from '@/hoc/with-permission';
import { AVAILABLE_EXTENSIONS } from './constants';
import { useActionPanelSearchParams } from '../use-action-panel-search-params';
import { actionPanelScrollToParamState } from '../action-panel.state';
import type { IGetRefs } from '@/hooks/use-multi-refs';
import { FilesList } from './files-list';
import { useSetAtom } from 'jotai';

interface FilesTabContentProps {
  files: File[];
  allowToChangeFilePrivacy?: boolean;
  emptyListLabel: string;
  onSubmit: (files: globalThis.File[]) => void;
  onClickDelete: (fileId: string, fileName: string) => void;
  onFileLinkDownload: FileLinkDownloadHandler;
  onShareFile: (payload: ShareFilePayload) => Promise<void>;
  readOnly?: boolean;
  useFileScanningStatus: (
    file: File,
  ) => UseQueryResult<FileScanning | undefined>;
}

const FilesDragDropWithPermission = withPermission(
  FilesDragDrop,
  PERMISSION.ADD_FILE,
);

const UploadFileInputWithPermission = withPermission(
  AutomaticUploadFileButton,
  PERMISSION.ADD_FILE,
);

export const FilesTabContent = withDraggableProvider(
  ({
    files,
    emptyListLabel,
    onClickDelete,
    onSubmit,
    onFileLinkDownload,
    onShareFile,
    useFileScanningStatus,
    allowToChangeFilePrivacy = true,
    readOnly = false,
  }: FilesTabContentProps) => {
    const [editedFileId, setEditedFileId] = useState<string | null>(null);
    const { hasRequiredPermission } = useUserPermissions();
    const setIsScrolled = useSetAtom(actionPanelScrollToParamState);
    const { fileParam } = useActionPanelSearchParams();

    const canDrag = !readOnly && hasRequiredPermission(PERMISSION.ADD_FILE);

    const [{ draggingItems }] = useDrag(
      {
        type: NativeTypes.FILE,
        canDrag,
        collect: monitor => ({
          draggingItems: monitor.getItem(),
        }),
      },
      [canDrag],
    );

    const scrollToFile: IGetRefs = refs => {
      if (!fileParam) return;

      const fileNode = refs[fileParam];

      if (!fileNode) return;

      fileNode.focus();
      fileNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
        inline: 'nearest',
      });

      setIsScrolled(true);
    };

    return (
      <TabContainer name="action-panel-wrapper" direction="column" grow={1}>
        {Boolean(draggingItems) && !readOnly && (
          <FilesDragDropWithPermission
            onDrop={onSubmit}
            allowedExtensions={AVAILABLE_EXTENSIONS}
          />
        )}
        {!readOnly && (
          <Flexbox
            name="action-panel-button-container"
            justify="right"
            alignItems="center"
            height="56px"
            padding="0 30px"
          >
            <UploadFileInputWithPermission
              onSubmit={onSubmit}
              acceptedFiles={AVAILABLE_EXTENSIONS}
            />
          </Flexbox>
        )}
        {isEmpty(files) ? (
          <ActionPanelEmpty image={SvgFiles} label={emptyListLabel}>
            {!readOnly && (
              <UploadFileInputWithPermission
                variant="primary"
                onSubmit={onSubmit}
                acceptedFiles={AVAILABLE_EXTENSIONS}
              />
            )}
          </ActionPanelEmpty>
        ) : (
          <FilesList
            files={files}
            getRefs={scrollToFile}
            editedFileId={editedFileId}
            editedFileIdSetter={setEditedFileId}
            onClickDelete={onClickDelete}
            onFileLinkDownload={onFileLinkDownload}
            onShareFile={onShareFile}
            useFileScanningStatus={useFileScanningStatus}
            allowToChangeFilePrivacy={allowToChangeFilePrivacy}
            readOnly={readOnly}
          />
        )}
      </TabContainer>
    );
  },
);

const TabContainer = styled(Flexbox)`
  position: relative;
`;
